import React from 'react'
import { money } from '../financial/formatting'
import { count } from '../staff-leads/model'
import { DELTA_COLOR, fmtDelta, rate, shareOfRevenue } from './model'

// The four headline numbers. Revenue leads; the two shares keep the colours
// they have everywhere else (--share-ours / --share-partner). Every delta is
// measured against `kpis.comparison`, printed exactly as the backend says it.
export default function HomeKpiTiles({ kpis }) {
  const comparison = kpis.comparison
  const ours = shareOfRevenue(kpis.our_share.value, kpis.revenue.value)
  const partners = shareOfRevenue(kpis.partners_share.value, kpis.revenue.value)
  const soldRate = kpis.sold_rate?.value

  return (
    <section aria-label="Key figures" className="hm-kpis">
      <div className="card" style={{ padding: 18, background: 'var(--kpi-hero-bg)', borderColor: 'var(--kpi-hero-bg)' }}>
        <TileLabel color="var(--kpi-hero-muted)">Revenue</TileLabel>
        <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--kpi-hero-fg)', lineHeight: 1.15 }}>{money(kpis.revenue.value)}</div>
        <Delta deltaPct={kpis.revenue.delta_pct} comparison={comparison} muted="var(--kpi-hero-muted)" />
      </div>
      <Tile
        label="Our share"
        swatch="var(--share-ours)"
        value={money(kpis.our_share.value)}
        caption={ours ? `${ours} of revenue` : null}
        delta={kpis.our_share.delta_pct}
        comparison={comparison}
      />
      <Tile
        label="Partners share"
        swatch="var(--share-partner)"
        value={money(kpis.partners_share.value)}
        caption={partners ? `${partners} of revenue` : null}
        delta={kpis.partners_share.delta_pct}
        comparison={comparison}
      />
      <Tile
        label="Leads received"
        value={count(kpis.leads_received.value)}
        caption={soldRate === null || soldRate === undefined ? null : `${rate(soldRate)} sold`}
        delta={kpis.leads_received.delta_pct}
        comparison={comparison}
      />
    </section>
  )
}

function Tile({ label, value, caption, swatch, delta, comparison }) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <TileLabel swatch={swatch}>{label}</TileLabel>
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.15 }}>{value}</div>
      {caption && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{caption}</div>}
      <Delta deltaPct={delta} comparison={comparison} />
    </div>
  )
}

// "↑ 12.3% vs 7-day avg". A null delta (nothing to compare against) shows
// nothing at all.
export function Delta({ deltaPct, comparison, muted = 'var(--text-muted)' }) {
  const d = fmtDelta(deltaPct)
  if (!d) return null
  return (
    <div data-testid="delta" style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12, marginTop: 6, flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 700, color: DELTA_COLOR[d.tone], fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{d.text}</span>
      {comparison && <span style={{ color: muted }}>{comparison}</span>}
    </div>
  )
}

function TileLabel({ children, swatch, color = 'var(--text-muted)' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color, marginBottom: 6 }}>
      {swatch && <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: swatch }} />}
      {children}
    </div>
  )
}
